// src/components/payment/PaymentPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { paymentService } from '../../services/paymentApi';

// UPDATED interface to match new backend response
interface PaymentData {
  status?: 'pending' | 'completed';
  token: string;
  amount_due: number;
  expires_at: string;
  hours_left: number;
  client: {
    id: number;
    full_name: string;
    phone: string;
    email: string;
    total_debt: number;
  };
  is_partial_payment: boolean;
  // NEW unified payment fields
  payment_type: 'credit_only' | 'exams_only' | 'mixed' | 'specific_items' | 'discounted_full' | 'arbitrary_partial';
  payment_description: string;
  credit_amount: number;
  exam_amount: number;
  // Discount fields for discounted_full payment type
  original_amount?: number;
  discount_amount?: number;
  discount_reason?: string;
  breakdown: {
    tasks: Array<{
      id: number;
      course_name: string;
      task_id: string;
      amount_owed: number;
      type?: string;
      created_at: string;
    }>;
    exams: Array<{
      id: number;
      course_name: string;
      exam_id: string;
      amount_owed: number;
      session_date?: string;
    }>;
    external: Array<{
      id: number;
      description: string;
      course_name?: string | null;
      amount: number;
      created_at: string;
    }>;
    task_count: number;
    exam_count: number;
    external_count: number;
  };
  // External charges amount
  external_amount?: number;
  // For completed payments
  payment_date?: string;
  amount_paid?: number;
  message?: string;
}

export const PaymentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);

  useEffect(() => {
    if (token) {
      loadPaymentData();
    }
  }, [token]);

  const loadPaymentData = async () => {
    if (!token) return;
    
    setLoading(true);
    const result = await paymentService.getPaymentData(token);
    
    if (result.success) {
      if (result.data.status === 'completed') {
        setShowSuccessPage(true);
      }
      setPaymentData(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handlePayment = async () => {
    if (!token) return;
    
    setProcessing(true);
    setError('');
    
    const result = await paymentService.processPayment(token);
    
    if (result.success) {
      window.location.href = result.data.payment_url;
    } else {
      setError(result.error);
      setProcessing(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3
    }).format(amount);
  };

  const getTimeRemaining = () => {
    if (!paymentData?.expires_at) return null;
    
    const expiryDate = new Date(paymentData.expires_at);
    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'منتهية الصلاحية';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} يوم متبقي`;
    } else if (hours > 0) {
      return `${hours} ساعة و ${minutes} دقيقة متبقية`;
    } else {
      return `${minutes} دقيقة متبقية`;
    }
  };

  // NEW: Get payment type display text
  const getPaymentTypeDisplay = () => {
    if (!paymentData) return '';

    switch (paymentData.payment_type) {
      case 'credit_only':
        return 'دفع مهام دراسية';
      case 'exams_only':
        return 'دفع رسوم امتحانات';
      case 'mixed':
        return 'دفع مهام وامتحانات';
      case 'specific_items':
        return 'دفع عناصر محددة';
      case 'discounted_full':
        return 'تسوية كاملة بخصم';
      case 'arbitrary_partial':
        return 'دفعة جزئية';
      default:
        return 'دفع مديونية';
    }
  };

  // Check if this is a discounted payment
  const isDiscountedPayment = paymentData?.payment_type === 'discounted_full' &&
    paymentData?.discount_amount && paymentData.discount_amount > 0;

  // NEW: Payment breakdown component
  const PaymentBreakdown: React.FC = () => {
    if (!paymentData?.breakdown) return null;

    const { breakdown } = paymentData;
    const hasMultipleTypes = breakdown.task_count > 0 && breakdown.exam_count > 0;

    return (
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          تفاصيل الدفع
        </h3>

        {/* Payment Summary */}
        <div className="grid grid-cols-1 gap-4 mb-4">
          {paymentData.credit_amount > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-blue-800 font-medium">
                  المهام الدراسية ({breakdown.task_count})
                </span>
                <span className="text-blue-900 font-bold">
                  {formatCurrency(paymentData.credit_amount)}
                </span>
              </div>
            </div>
          )}

          {paymentData.exam_amount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-green-800 font-medium">
                  رسوم الامتحانات ({breakdown.exam_count})
                </span>
                <span className="text-green-900 font-bold">
                  {formatCurrency(paymentData.exam_amount)}
                </span>
              </div>
            </div>
          )}

          {(paymentData.external_amount ?? 0) > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-purple-800 font-medium">
                  رسوم إضافية ({breakdown.external_count})
                </span>
                <span className="text-purple-900 font-bold">
                  {formatCurrency(paymentData.external_amount ?? 0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Detailed Items */}
        {(breakdown.tasks.length > 0 || breakdown.exams.length > 0 || (breakdown.external?.length ?? 0) > 0) && (
          <details className="group">
            <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 font-medium">
              عرض التفاصيل الكاملة
              <span className="float-left group-open:rotate-180 transition-transform">▼</span>
            </summary>

            <div className="mt-4 space-y-3">
              {/* Tasks */}
              {breakdown.tasks.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">المهام الدراسية:</h4>
                  <div className="space-y-2">
                    {breakdown.tasks.map((task) => (
                      <div key={task.id} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{task.course_name}</p>
                            <p className="text-sm text-gray-600">رقم المهمة: {task.task_id}</p>
                            {task.type && (
                              <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mt-1">
                                {task.type}
                              </span>
                            )}
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-gray-900">{formatCurrency(task.amount_owed)}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(task.created_at).toLocaleDateString('ar-JO')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Exams */}
              {breakdown.exams.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">الامتحانات:</h4>
                  <div className="space-y-2">
                    {breakdown.exams.map((exam) => (
                      <div key={exam.id} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{exam.course_name}</p>
                            <p className="text-sm text-gray-600">رقم الامتحان: {exam.exam_id}</p>
                            {exam.session_date && (
                              <p className="text-xs text-gray-500">
                                تاريخ الجلسة: {new Date(exam.session_date).toLocaleDateString('ar-JO')}
                              </p>
                            )}
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-gray-900">{formatCurrency(exam.amount_owed)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* External Charges */}
              {(breakdown.external?.length ?? 0) > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">رسوم إضافية:</h4>
                  <div className="space-y-2">
                    {breakdown.external.map((charge) => (
                      <div key={charge.id} className="bg-white border border-purple-200 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{charge.description}</p>
                            {charge.course_name && (
                              <p className="text-sm text-gray-600 mt-1">المادة: {charge.course_name}</p>
                            )}
                            <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full mt-1">
                              رسوم خارجية
                            </span>
                          </div>
                          <div className="text-left">
                            <p className="font-bold text-purple-900">{formatCurrency(charge.amount)}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(charge.created_at).toLocaleDateString('ar-JO')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل بيانات الدفع...</p>
        </div>
      </div>
    );
  }

  if (error && !paymentData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">رابط دفع غير صالح</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.close()}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            إغلاق النافذة
          </button>
        </div>
      </div>
    );
  }

  // Success page for completed payments (keep existing code)
  if (showSuccessPage && paymentData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center py-8 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold">تم الدفع بنجاح!</h1>
            <p className="text-green-100 mt-2">{getPaymentTypeDisplay()}</p>
          </div>

          <div className="p-6">
            {/* Payment Info */}
            <div className="text-center mb-6">
              <div className="bg-green-50 rounded-xl p-6 mb-4">
                <p className="text-green-600 font-medium mb-2">المبلغ المدفوع</p>
                <p className="text-3xl font-bold text-green-700">
                  {paymentData.amount_paid ? formatCurrency(paymentData.amount_paid) : formatCurrency(paymentData.amount_due)}
                </p>
              </div>
              
              {paymentData.payment_description && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-800">{paymentData.payment_description}</p>
                </div>
              )}
              
              {paymentData.payment_date && (
                <p className="text-gray-600 text-sm">
                  تاريخ الدفع: {new Date(paymentData.payment_date).toLocaleDateString('ar-JO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>

            {/* Client Info */}
            <div className="border-t border-gray-200 pt-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-2">تفاصيل الحساب</h3>
              <p className="text-gray-600">العميل: {paymentData.client.full_name}</p>
              {paymentData.message && (
                <p className="text-sm text-gray-500 mt-2">{paymentData.message}</p>
              )}
            </div>

            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-green-800 mb-2 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                تم بنجاح
              </h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• تم استلام الدفعة وتسجيلها في النظام</li>
                <li>• تم تحديث رصيد حسابك</li>
                <li>• ستصلك رسالة تأكيد قريباً</li>
              </ul>
            </div>

            {/* Close Button */}
            <button
              onClick={() => window.close()}
              className="w-full bg-gray-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-gray-700 transition-colors"
            >
              إغلاق النافذة
            </button>

            {/* Support Info */}
            <div className="text-center mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                شكراً لك على الدفع!
                <br />
                للاستفسارات: <span className="font-medium">+965-56501847</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal payment form
  const timeRemaining = getTimeRemaining();
  const isExpiringSoon = paymentData?.hours_left !== undefined && paymentData.hours_left <= 24;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold">{getPaymentTypeDisplay()}</h1>
            <p className="text-blue-100 mt-2">دفع آمن عبر MyFatoorah</p>
          </div>

          <div className="p-6">
            {/* Expiration Warning */}
            {isExpiringSoon && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-amber-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-amber-800">ينتهي الرابط قريباً</p>
                    <p className="text-sm text-amber-700">{timeRemaining}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* Client Info */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                معلومات الحساب
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">الاسم:</span>
                  <p className="font-medium">{paymentData?.client?.full_name}</p>
                </div>
                <div>
                  <span className="text-gray-600">الهاتف:</span>
                  <p className="font-medium">{paymentData?.client?.phone}</p>
                </div>
              </div>
            </div>

            {/* Discount Banner for discounted_full payments */}
            {isDiscountedPayment && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center ml-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-green-800 text-lg">خصم خاص لك!</h3>
                    <p className="text-sm text-green-700">سيتم إغلاق جميع المديونية عند الدفع</p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center text-gray-700">
                    <span>المبلغ الأصلي:</span>
                    <span className="line-through">{formatCurrency(paymentData?.original_amount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center text-green-600 font-medium">
                    <span>الخصم:</span>
                    <span>- {formatCurrency(paymentData?.discount_amount || 0)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                    <span className="font-bold text-gray-900">المطلوب دفعه:</span>
                    <span className="font-bold text-green-700 text-xl">{formatCurrency(paymentData?.amount_due || 0)}</span>
                  </div>
                </div>

                {paymentData?.discount_reason && (
                  <p className="text-sm text-green-700 mt-3 bg-green-100 rounded-lg p-2">
                    <span className="font-medium">سبب الخصم:</span> {paymentData.discount_reason}
                  </p>
                )}

                <div className="mt-4 flex items-center text-green-700 text-sm">
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>عند الدفع سيتم تسوية كامل المديونية وإغلاق جميع المهام والامتحانات المستحقة</span>
                </div>
              </div>
            )}

            {/* Payment Breakdown - only show for non-discounted payments */}
            {!isDiscountedPayment && <PaymentBreakdown />}

            {/* Payment Amount - different display for discounted vs regular */}
            {!isDiscountedPayment && (
              <div className="bg-gray-50 rounded-xl p-6 mb-6 text-center">
                <p className="text-gray-600 mb-2">إجمالي المبلغ المطلوب</p>
                <p className="text-3xl font-bold text-blue-600">
                  {paymentData?.amount_due ? formatCurrency(paymentData.amount_due) : ''}
                </p>
                {paymentData?.is_partial_payment && paymentData?.payment_type !== 'arbitrary_partial' && (
                  <p className="text-sm text-gray-500 mt-2">
                    (دفعة جزئية - إجمالي المديونية: {paymentData?.client?.total_debt ? formatCurrency(paymentData.client.total_debt) : ''})
                  </p>
                )}
                {paymentData?.payment_type === 'arbitrary_partial' && (
                  <p className="text-sm text-amber-600 mt-2">
                    (دفعة جزئية - سيتم توزيعها على أقدم المديونيات أولاً)
                  </p>
                )}
              </div>
            )}

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={processing}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                  جاري التحويل للدفع...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  ادفع بأمان الآن
                </>
              )}
            </button>

            {/* Security Info */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
              <h4 className="font-medium text-green-800 mb-2 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                دفع آمن
              </h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• يتم معالجة الدفع عبر MyFatoorah (بوابة دفع آمنة)</li>
                <li>• معلومات الدفع محمية ومشفرة</li>
                <li>• ستحصل على تأكيد فور اكتمال الدفع</li>
                <li>• ينتهي صلاحية هذا الرابط في {timeRemaining}</li>
              </ul>
            </div>

            {/* Support Info */}
            <div className="text-center mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                تحتاج مساعدة؟ اتصل بفريق الدعم
                <br />
                <span className="font-medium">+965-56501847</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};